/**
 * The open campaign GROUP has no master config: one button in the
 * containers card header. It opens three ways to make one — blank; a COPY
 * of another group's master config (opened in the editor on this group,
 * edit, then save); or from one of the group's containers (its settings
 * become the config and it follows it).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Crown } from 'lucide-react';
import type { DropGenMasterSummary } from '@trafficpulse/shared';

import { Button } from '@/components/ui';

import type { DropGenContainerSummary } from '../../../../drop-generator.types';
import { middleTruncate } from '../../campaign-scope';
import { containerOption, masterConfigOption } from '../../options/select-options';
import { MakeMasterDialog } from './MakeMasterDialog';
import { PickAndGo } from './PickAndGo';
import { useMasterConfigActions } from '../use-master-config-actions';

interface Props {
  groupLabel: string;
  /** This campaign's containers that follow nothing yet. */
  standalone: DropGenContainerSummary[];
  /** Master configs of the other groups — copy sources. */
  others: DropGenMasterSummary[];
  groupLabelOf: (key: string) => string;
  onCreate: () => void;
  onCopy: (masterId: string) => void;
}

export const CreateMasterConfigButton: FC<Props> = ({ groupLabel, standalone, others, groupLabelOf, onCreate, onCopy }) => {
  const actions = useMasterConfigActions();
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState<DropGenContainerSummary | null>(null);
  const go = (fn: () => void) => {
    setOpen(false);
    fn();
  };
  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className="flex h-9 items-center gap-2 rounded-md border border-dashed border-primary/50 bg-primary/5 px-3 text-sm font-medium text-primary transition-colors hover:bg-primary/10">
            <Crown className="h-3.5 w-3.5" /> Create master config
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={6} collisionPadding={12} onOpenAutoFocus={(e) => e.preventDefault()} className="z-[250] flex w-[min(400px,92vw)] flex-col rounded-lg border border-border bg-popover shadow-lg">
            <div className="flex flex-col gap-1 border-b border-border px-4 py-3">
              <span className="text-sm font-semibold" title={groupLabel}>Master config for {middleTruncate(groupLabel, 32)}</span>
              <span className="text-xs text-muted-foreground">Every container of this campaign group follows it — on its own campaign of the group — and can still override any section.</span>
            </div>
            <div className="flex flex-col gap-3 px-4 py-3">
              <Button size="sm" onClick={() => go(onCreate)}>Start from scratch</Button>
              {others.length > 0 ? (
                <PickAndGo
                  label="Copy another group's master config — edit it, then save"
                  placeholder="Pick a master config…"
                  options={others.map((m) => masterConfigOption(m, groupLabelOf(m.campaignGroupKey)))}
                  action="Copy & edit"
                  onGo={(id) => go(() => onCopy(id))}
                />
              ) : null}
              {standalone.length > 0 ? (
                <PickAndGo
                  label="Make it from one of this group's containers — it then follows it"
                  placeholder="Pick a container…"
                  options={standalone.map(containerOption)}
                  action="Use it"
                  onGo={(id) => go(() => setNaming(standalone.find((c) => c.id === id) ?? null))}
                />
              ) : null}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {naming ? (
        <MakeMasterDialog open containerName={naming.name} busy={actions.promoting} onConfirm={async (name) => (await actions.promote(naming, name)) && setNaming(null)} onCancel={() => setNaming(null)} />
      ) : null}
    </>
  );
};
